import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, ArrowDownLeft, ArrowUpRight, Receipt, ChevronRight, Sun, CalendarClock, Printer, Play, X, Wifi, WifiOff, RefreshCw, Users, BookUser, Truck, Undo2, Tag, Store, BarChart3, TrendingUp, RefreshCcw } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { POS_T } from "@/lib/posI18n";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminPOSHome({ lang = "en", setLang, onNewSale, onDayClose, onOpenPrinter, holds = [], onResumeHold, onDropHold, online = true, queued = 0, onSync }) {
  const t = (k) => POS_T[lang]?.[k] ?? POS_T.en[k] ?? k;
  const navigate = useNavigate();
  const navTo = (p) => { setMenuOpen(false); navigate(p); };
  const [dash, setDash] = useState(null);
  const [txns, setTxns] = useState([]);
  const [parties, setParties] = useState([]);
  const [payables, setPayables] = useState({ suppliers: [], total_payable: 0 });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("txns");
  const [menuOpen, setMenuOpen] = useState(false);
  const [payIn, setPayIn] = useState(null);   // {phone,name,amount,note}
  const [payOut, setPayOut] = useState(null); // {supplier_id,amount,note}
  const go = (fn) => { setMenuOpen(false); fn?.(); };

  const refresh = useCallback(() => {
    api.get("/admin/tally-dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setTxns(r.data)).catch(() => {});
    api.get("/pos/parties").then((r) => setParties(r.data)).catch(() => {});
    api.get("/admin/supplier-payables").then((r) => setPayables(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const monthSales = (() => {
    const ym = new Date().toISOString().slice(0, 7);
    return (txns || []).filter((o) => (o.created_at || "").slice(0, 7) === ym).reduce((s, o) => s + (o.total || 0), 0);
  })();

  const shown = (txns || []).filter((o) => {
    if (filter === "sale" && o.type !== "pos" && o.type !== "online") return false;
    if (filter === "credit" && !(o.credit_amount > 0)) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (o.customer_name || "").toLowerCase().includes(s) || (o.invoice_no || "").toLowerCase().includes(s) ||
      (o.order_no || "").toLowerCase().includes(s) || (o.customer_phone || "").includes(q);
  });

  const shownParties = (parties || []).filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) || (p.phone || "").includes(q);
  });

  const statusOf = (o) => {
    if (o.payment_status === "credit" || o.credit_amount >= (o.total || 0)) return { txt: t("unpaid"), cls: "bg-amber-100 text-amber-700" };
    if (o.payment_status === "partial") return { txt: t("partial"), cls: "bg-orange-100 text-orange-700" };
    return { txt: t("paid"), cls: "bg-emerald-100 text-emerald-700" };
  };

  const submitPayIn = async () => {
    const amt = Number(payIn.amount) || 0;
    if (!payIn.phone) return toast.error(t("select_party"));
    if (amt <= 0) return toast.error(t("amount"));
    try {
      await api.post("/admin/khata/pay", { phone: payIn.phone, name: payIn.name, amount: amt, note: payIn.note || null });
      toast.success(`${inr(amt)} ${t("payment_in")}`); setPayIn(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const submitPayOut = async () => {
    const amt = Number(payOut.amount) || 0;
    if (!payOut.supplier_id) return toast.error(t("select_supplier"));
    if (amt <= 0) return toast.error(t("amount"));
    try {
      await api.post("/admin/supplier-payment", { supplier_id: payOut.supplier_id, amount: amt, note: payOut.note || null });
      toast.success(`${inr(amt)} ${t("payment_out")}`); setPayOut(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="pos-home">
      {/* Brand header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <button data-testid="pos-logo-menu-btn" onClick={() => setMenuOpen(true)} className="rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 active:scale-95 transition-transform" title={t("menu")}>
            <img src="/rsmart-pos-logo.png" alt="Rs. Mart POS" className="w-11 h-11 rounded-full object-contain bg-white shadow" />
          </button>
          <div className="leading-tight">
            <p className="text-lg font-extrabold text-slate-900">Rs. MART POS <span className="text-amber-500 text-xs align-top">★ {t("pro")}</span></p>
            <p className="text-xs font-semibold text-slate-500">{t("store")} · All Stores</p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-0.5" data-testid="pos-lang-toggle">
          {["en", "hi"].map((l) => (
            <button key={l} data-testid={`pos-lang-${l}`} onClick={() => setLang(l)}
              className={`px-3 py-1 rounded-full text-xs font-bold ${lang === l ? "bg-emerald-600 text-white" : "text-slate-500"}`}>
              {l === "en" ? "EN" : "हिं"}
            </button>
          ))}
        </div>
      </div>

      {/* Green summary card */}
      <div className="rounded-2xl p-5 text-white shadow-lg" style={{ background: "linear-gradient(135deg,#2f5330,#3d6b3d)" }} data-testid="pos-summary-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-1.5 text-white/80 text-sm font-semibold"><Sun size={15} /> {t("todays_sales")}</p>
            <p className="text-3xl font-extrabold mt-1" data-testid="pos-today-sales">{inr(dash?.today_sales ?? 0)}</p>
            <p className="text-white/70 text-xs mt-0.5">{dash?.orders_count ?? 0} {t("bills")} · {inr(monthSales)} {t("this_month")}</p>
          </div>
          <div className="bg-white/10 rounded-xl px-3 py-2 flex gap-4">
            <div><p className="text-white/70 text-[11px]">{t("to_receive")}</p><p className="font-extrabold text-emerald-300" data-testid="pos-to-receive">{inr(dash?.receivables ?? 0)}</p></div>
            <div className="border-l border-white/20 pl-4"><p className="text-white/70 text-[11px]">{t("to_pay")}</p><p className="font-extrabold text-amber-300" data-testid="pos-to-pay">{inr(dash?.to_pay ?? payables.total_payable ?? 0)}</p></div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <button data-testid="pos-payment-in-chip" onClick={() => setPayIn({ phone: "", name: "", amount: "", note: "" })} className="flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"><ArrowDownLeft size={13} /> {t("payment_in")}</button>
          <button data-testid="pos-payment-out-chip" onClick={() => setPayOut({ supplier_id: "", amount: "", note: "" })} className="flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"><ArrowUpRight size={13} /> {t("payment_out")}</button>
          <button data-testid="pos-billing-chip" onClick={onNewSale} className="flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"><Receipt size={13} /> {t("pos_billing")}</button>
          <button data-testid="pos-new-sale-btn" onClick={onNewSale} className="ml-auto flex items-center gap-1.5 bg-white text-emerald-800 rounded-full px-4 py-1.5 text-sm font-extrabold hover:bg-emerald-50">
            <Plus size={16} /> {t("new_sale")}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button data-testid="pos-tab-txns" onClick={() => setTab("txns")} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold ${tab === "txns" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}><Receipt size={15} /> {t("transactions")}</button>
        <button data-testid="pos-tab-parties" onClick={() => setTab("parties")} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold ${tab === "parties" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}><Users size={15} /> {t("party_details")}</button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="pos-home-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === "parties" ? t("select_party") : t("search_txn")}
          className="w-full pl-10 pr-3 py-3 bg-white border border-slate-200 rounded-xl text-sm" />
      </div>

      {tab === "txns" ? (
        <>
          {/* Filter chips */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[["all", t("all")], ["sale", t("sale")], ["credit", t("credit_sale")]].map(([k, label]) => (
              <button key={k} data-testid={`pos-txfilter-${k}`} onClick={() => setFilter(k)}
                className={`px-4 py-1.5 rounded-full text-sm font-bold whitespace-nowrap ${filter === k ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{label}</button>
            ))}
          </div>
          {/* Transactions */}
          <div className="space-y-3">
            {shown.length === 0 && <p className="text-center text-slate-400 text-sm py-10">{t("no_txns")}</p>}
            {shown.map((o) => {
              const st = statusOf(o);
              const bal = o.credit_amount > 0 ? o.credit_amount : 0;
              return (
                <button key={o.id} data-testid={`pos-txn-${o.id}`} onClick={() => window.open(`/print/invoice/${o.id}`, "_blank")}
                  className="w-full text-left bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:border-emerald-300 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold text-slate-900 line-clamp-1">{o.customer_name || "Walk-in"}</p>
                    <span className="text-xs text-slate-400 shrink-0">{o.invoice_no || o.order_no}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded ${st.cls}`}>{t("sale").toUpperCase()} : {st.txt}</span>
                    <span className="text-xs text-slate-400">{(o.created_at || "").slice(0, 10)}</span>
                    {o.due_date && bal > 0 && <span className="text-[11px] font-bold text-red-500">{t("due_on")}: {o.due_date}</span>}
                  </div>
                  <div className="flex items-end justify-between mt-3 pt-3 border-t border-slate-100">
                    <div><p className="text-xs text-slate-400">{t("total")}</p><p className="font-extrabold text-slate-900">{inr(o.total)}</p></div>
                    <div className="text-right"><p className="text-xs text-slate-400">{t("balance")}</p><p className={`font-extrabold ${bal > 0 ? "text-red-600" : "text-emerald-600"}`}>{inr(bal)}</p></div>
                    <ChevronRight size={18} className="text-slate-300 mb-1" />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        /* Party Details tab */
        <div className="space-y-3" data-testid="pos-parties-list">
          {shownParties.length === 0 && <p className="text-center text-slate-400 text-sm py-10">{t("no_parties")}</p>}
          {shownParties.map((p) => (
            <div key={p.phone} data-testid={`pos-party-${p.phone}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 line-clamp-1">{p.name}</p>
                  <p className="text-xs text-slate-400">{p.phone}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-slate-400">{p.due > 0 ? t("to_receive") : t("balance")}</p>
                  <p className={`font-extrabold ${p.due > 0 ? "text-red-600" : "text-emerald-600"}`}>{inr(Math.abs(p.due))}</p>
                </div>
              </div>
              {p.due > 0 && (
                <button data-testid={`pos-party-collect-${p.phone}`} onClick={() => setPayIn({ phone: p.phone, name: p.name, amount: String(p.due), note: "" })}
                  className="mt-3 w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-1.5">
                  <ArrowDownLeft size={14} /> {t("collect_due")} {inr(p.due)}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* -------- POS menu drawer (logo click) -------- */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="bg-white w-[300px] sm:max-w-[320px] p-0 flex flex-col" data-testid="pos-menu-drawer">
          <SheetHeader className="p-4 border-b border-slate-100 flex-row items-center gap-2.5 space-y-0">
            <img src="/rsmart-pos-logo.png" alt="Rs. Mart POS" className="w-10 h-10 rounded-full object-contain bg-white shadow" />
            <SheetTitle className="leading-tight text-left">
              <span className="block text-base font-extrabold text-slate-900">Rs. MART POS <span className="text-amber-500 text-[10px] align-top">★ {t("pro")}</span></span>
              <span className="block text-[11px] font-semibold text-slate-500">{t("store")} · {t("menu")}</span>
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
            <p className="px-1 pb-0.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Billing</p>
            <button data-testid="pos-menu-new-sale" onClick={() => go(onNewSale)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700"><Plus size={16} /> {t("new_sale")}</button>
            <button data-testid="pos-menu-party-details" onClick={() => go(() => setTab("parties"))} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"><Users size={16} /> {t("party_details")}</button>
            <button data-testid="pos-menu-payment-in" onClick={() => go(() => setPayIn({ phone: "", name: "", amount: "", note: "" }))} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"><ArrowDownLeft size={16} className="text-emerald-600" /> {t("payment_in")}</button>
            <button data-testid="pos-menu-payment-out" onClick={() => go(() => setPayOut({ supplier_id: "", amount: "", note: "" }))} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"><ArrowUpRight size={16} className="text-amber-600" /> {t("payment_out")}</button>
            <button data-testid="pos-menu-day-close" onClick={() => go(onDayClose)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"><CalendarClock size={16} /> {t("day_close")}</button>
            <button data-testid="pos-menu-printer" onClick={() => go(onOpenPrinter)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"><Printer size={16} /> {t("printer_settings")}</button>

            <p className="px-1 pt-3 pb-0.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Manage</p>
            {[
              ["pos-menu-khata", <BookUser size={16} key="i" />, "Khata / Udhaar", "/admin/khata"],
              ["pos-menu-suppliers", <Truck size={16} key="i" />, "Suppliers & Purchases", "/admin/suppliers"],
              ["pos-menu-preturns", <Undo2 size={16} key="i" />, "Purchase Returns", "/admin/purchase-returns"],
              ["pos-menu-labels", <Tag size={16} key="i" />, "Barcode Labels", "/admin/labels"],
              ["pos-menu-business", <Store size={16} key="i" />, "Business Settings", "/admin/business"],
              ["pos-menu-analytics", <BarChart3 size={16} key="i" />, "Sales Analytics", "/admin/analytics"],
              ["pos-menu-profit", <TrendingUp size={16} key="i" />, "Profit Margins", "/admin/profit"],
              ["pos-menu-sync", <RefreshCcw size={16} key="i" />, "App & POS Sync", "/admin/sync"],
            ].map(([tid, icon, label, path]) => (
              <button key={tid} data-testid={tid} onClick={() => navTo(path)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50">{icon} {label}</button>
            ))}

            <div className="pt-3">
              <p className="px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{t("held_bills")} ({holds.length})</p>
              {holds.length === 0 && <p className="px-1 text-xs text-slate-400">{t("no_holds")}</p>}
              <div className="space-y-1.5">
                {holds.map((h) => (
                  <div key={h.id} data-testid={`pos-menu-hold-${h.id}`} className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-xl px-2.5 py-2">
                    <button onClick={() => go(() => onResumeHold?.(h))} className="flex-1 flex items-center gap-1.5 text-left text-xs font-bold text-amber-800 min-w-0"><Play size={12} className="shrink-0" /> <span className="truncate">{h.label} · {inr(h.total)}</span></button>
                    <button onClick={() => onDropHold?.(h.id)} className="text-amber-400 hover:text-red-500 shrink-0"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="p-3 border-t border-slate-100 space-y-2.5">
            <div className="flex items-center justify-between">
              <span data-testid="pos-menu-status" className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold ${online ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{online ? <Wifi size={13} /> : <WifiOff size={13} />} {online ? t("online") : t("offline")}</span>
              {queued > 0 && <button data-testid="pos-menu-sync" onClick={() => go(onSync)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-amber-100 text-amber-800"><RefreshCw size={12} /> {queued} · {t("sync_now")}</button>}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-500">Language</span>
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5" data-testid="pos-menu-lang-toggle">
                {["en", "hi"].map((l) => (<button key={l} data-testid={`pos-menu-lang-${l}`} onClick={() => setLang(l)} className={`px-3 py-1 rounded-full text-xs font-bold ${lang === l ? "bg-emerald-600 text-white" : "text-slate-500"}`}>{l === "en" ? "EN" : "हिं"}</button>))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* -------- Payment In modal (collect from customer) -------- */}
      <Dialog open={!!payIn} onOpenChange={(o) => !o && setPayIn(null)}>
        <DialogContent className="bg-white max-w-sm" data-testid="pos-payin-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-700"><ArrowDownLeft size={18} /> {t("record_payment_in")}</DialogTitle></DialogHeader>
          {payIn && (
            <div className="space-y-3">
              <label className="block text-xs font-bold text-slate-500">{t("select_party")}
                <select data-testid="payin-party" value={payIn.phone} onChange={(e) => { const p = parties.find((x) => x.phone === e.target.value); setPayIn({ ...payIn, phone: e.target.value, name: p?.name || "", amount: p && p.due > 0 ? String(p.due) : payIn.amount }); }}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800">
                  <option value="">— {t("select_party")} —</option>
                  {parties.map((p) => <option key={p.phone} value={p.phone}>{p.name} · {p.phone}{p.due > 0 ? ` · ${t("due_on")} ${inr(p.due)}` : ""}</option>)}
                </select>
              </label>
              <label className="block text-xs font-bold text-slate-500">{t("amount")}<input data-testid="payin-amount" type="number" value={payIn.amount} onChange={(e) => setPayIn({ ...payIn, amount: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></label>
              <label className="block text-xs font-bold text-slate-500">{t("note_opt")}<input data-testid="payin-note" value={payIn.note} onChange={(e) => setPayIn({ ...payIn, note: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></label>
              <div className="flex gap-2">
                <button onClick={() => setPayIn(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">{t("cancel")}</button>
                <button data-testid="payin-save" onClick={submitPayIn} className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm">{t("save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* -------- Payment Out modal (pay supplier) -------- */}
      <Dialog open={!!payOut} onOpenChange={(o) => !o && setPayOut(null)}>
        <DialogContent className="bg-white max-w-sm" data-testid="pos-payout-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-700"><ArrowUpRight size={18} /> {t("record_payment_out")}</DialogTitle></DialogHeader>
          {payOut && (
            <div className="space-y-3">
              <label className="block text-xs font-bold text-slate-500">{t("select_supplier")}
                <select data-testid="payout-supplier" value={payOut.supplier_id} onChange={(e) => { const s = payables.suppliers.find((x) => x.id === e.target.value); setPayOut({ ...payOut, supplier_id: e.target.value, amount: s && s.payable > 0 ? String(s.payable) : payOut.amount }); }}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800">
                  <option value="">— {t("select_supplier")} —</option>
                  {payables.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.payable > 0 ? ` · ${t("payable")} ${inr(s.payable)}` : ""}</option>)}
                </select>
              </label>
              <label className="block text-xs font-bold text-slate-500">{t("amount")}<input data-testid="payout-amount" type="number" value={payOut.amount} onChange={(e) => setPayOut({ ...payOut, amount: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></label>
              <label className="block text-xs font-bold text-slate-500">{t("note_opt")}<input data-testid="payout-note" value={payOut.note} onChange={(e) => setPayOut({ ...payOut, note: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></label>
              <div className="flex gap-2">
                <button onClick={() => setPayOut(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">{t("cancel")}</button>
                <button data-testid="payout-save" onClick={submitPayOut} className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold text-sm">{t("save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
